import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MessageCircle, Instagram, Facebook, Linkedin, FileText, Flame } from "lucide-react";

// Shared card shell — every "receipt" lands inside the chat as a rounded card
const Shell = ({ children, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 12, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
    role="figure"
    aria-label={label}
    className="w-full max-w-[260px] bg-bone border border-ink/10 rounded-xl overflow-hidden shadow-sm"
  >
    {children}
  </motion.div>
);

// Act 1 — before/after staging crossfade + style chips
export const CardStaging = () => {
  const [after, setAfter] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAfter(true), 1600); return () => clearTimeout(t); }, []);
  return (
    <Shell label="Staged living room result">
      <div className="relative aspect-[16/10] bg-ink/5">
        <img src="/staging/before.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <motion.img src="/staging/after-modern.png" alt="" initial={{ opacity: 0 }} animate={{ opacity: after ? 1 : 0 }} transition={{ duration: 1.6 }} className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="flex gap-1.5 p-2.5 text-[10px]">
        {["Modern", "Scandi", "Coastal"].map((s, i) => (
          <span key={s} className={`px-2 py-1 border ${i === 0 ? "border-coral text-coral animate-pulse" : "border-ink/15 text-ink/50"}`}>{s}</span>
        ))}
      </div>
    </Shell>
  );
};

// Act 2 — mini comps map with 6 pins dropping one-by-one
export const CardComps = () => {
  const [n, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN((v) => Math.min(6, v + 1)), 150); return () => clearInterval(t); }, []);
  const pins = [[24, 34], [52, 22], [70, 44], [38, 58], [78, 66], [18, 72]];
  return (
    <Shell label="Neighborhood comparables map">
      <div className="relative aspect-[16/10] bg-gradient-to-br from-[#EDE4D3] via-[#E5D4B8] to-[#D6C09A] overflow-hidden">
        {/* faux street grid */}
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(11,11,15,.15) 1px,transparent 1px),linear-gradient(90deg,rgba(11,11,15,.15) 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
        {pins.slice(0, n).map(([x, y], i) => (
          <motion.div key={i} initial={{ opacity: 0, y: -14, scale: 0.6 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 320, damping: 18 }} className="absolute w-5 h-5 -translate-x-1/2 -translate-y-full" style={{ left: `${x}%`, top: `${y}%` }}>
            <div className="w-5 h-5 bg-coral rounded-full flex items-center justify-center text-[9px] text-bone font-bold shadow-lg">{i + 1}</div>
          </motion.div>
        ))}
        <div className="absolute top-2 right-2 bg-ink/85 text-bone text-[9px] px-2 py-1 rounded">
          {n < 6 ? `Loading ${n}/6…` : "Median $842k · You +2.1%"}
        </div>
      </div>
    </Shell>
  );
};

// Act 3 — panorama capture sweep + seamless infinite auto-pan
export const CardTour = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setReady(true), 2600); return () => clearTimeout(t); }, []);
  return (
    <Shell label="360-degree tour capture">
      <div className="relative aspect-[16/10] bg-ink overflow-hidden">
        {/* Equirectangular pano wraps seamlessly via repeat-x — no black seam */}
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/panos/living.jpg)",
            backgroundSize: "auto 100%",
            backgroundRepeat: "repeat-x",
          }}
          animate={ready ? { backgroundPositionX: ["0%", "-200%"] } : { backgroundPositionX: "0%" }}
          transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
        />
        {!ready && (
          <motion.div initial={{ x: "-100%" }} animate={{ x: "100%" }} transition={{ duration: 2.2, ease: "easeInOut" }} className="absolute inset-y-0 w-16 border-2 border-dashed border-coral/80" />
        )}
        <div className="absolute top-2 left-2 bg-ink/85 text-bone text-[9px] px-2 py-1 rounded">
          {ready ? "Tour ready ✓" : "Stitching 5 frames…"}
        </div>
      </div>
    </Shell>
  );
};

// Act 4 — 6 promo tiles cascading in
const PROMO = [
  { Icon: Mail, ch: "Email", copy: "The staircase in this house is a museum piece." },
  { Icon: MessageCircle, ch: "SMS", copy: "1247 Waverley just hit. First tour Sat 11am — free?" },
  { Icon: Instagram, ch: "IG", copy: "Sun-soaked. Kid-friendly. Priced to move." },
  { Icon: Facebook, ch: "FB", copy: "Just listed. Under 2 miles from Sir John A." },
  { Icon: Linkedin, ch: "LI", copy: "Case study: Halifax waterfront that traded 8 days over ask." },
  { Icon: FileText, ch: "Flyer", copy: "Open House · Sat 11-1 · Bring the family." },
];
export const CardPromo = () => (
  <Shell label="Launch campaign kit">
    <div className="p-2.5 grid grid-cols-2 gap-1.5">
      {PROMO.map((p, i) => (
        <motion.div key={p.ch} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.2, duration: 0.25 }} className="border border-ink/10 p-2">
          <div className="flex items-center gap-1.5 mb-1">
            <p.Icon className="w-3 h-3 text-coral" />
            <span className="text-[9px] uppercase tracking-wide text-ink/50">{p.ch}</span>
          </div>
          <p className="text-[10px] text-ink/75 leading-tight line-clamp-2">{p.copy}</p>
        </motion.div>
      ))}
    </div>
  </Shell>
);

// Act 5 — radial score ring 0→9 + signal chips + HOT pill
export const CardLead = () => {
  const [score, setScore] = useState(0);
  useEffect(() => { let x = 0; const t = setInterval(() => { x += 1; setScore(x); if (x >= 9) clearInterval(t); }, 155); return () => clearInterval(t); }, []);
  const pct = (score / 10) * 100;
  const r = 26, C = 2 * Math.PI * r;
  return (
    <Shell label="Lead score card">
      <div className="p-3 flex items-center gap-3">
        <div className="relative w-16 h-16 shrink-0">
          <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
            <circle cx="32" cy="32" r={r} stroke="rgba(11,11,15,.1)" strokeWidth="6" fill="none" />
            <circle cx="32" cy="32" r={r} stroke="#FF5A36" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C - (pct / 100) * C} style={{ transition: "stroke-dashoffset 0.15s linear" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-serif text-lg text-ink">{score}<span className="text-[9px] text-ink/40">/10</span></div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <img src="https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=96&h=96&fit=crop" alt="" className="w-6 h-6 rounded-full object-cover" />
            <span className="inline-flex items-center gap-1 bg-coral text-bone text-[9px] px-1.5 py-0.5 rounded"><Flame className="w-2.5 h-2.5" /> HOT</span>
          </div>
          <AnimatePresence>
            {["Pre-approved", "Timeline: 30d", "Objection: HOA fee"].map((c, i) => (
              <motion.div key={c} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.3 }} className="text-[9px] text-ink/70 truncate">• {c}</motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </Shell>
  );
};

// Act 6 — 3 competitor "move" rows sliding up
const MOVES = [
  { thumb: "linear-gradient(135deg,#3b2a1a,#7a5033)", copy: "Rooftop-video hook · 68k views" },
  { thumb: "linear-gradient(135deg,#1b3242,#3c6a86)", copy: "School-zone Story reel · 12k likes" },
  { thumb: "linear-gradient(135deg,#4a2436,#8a4a5f)", copy: "'Just sold in 4 days' carousel" },
];
export const CardCompetitor = () => (
  <Shell label="Competitor moves this week">
    <div className="p-2 space-y-1.5">
      {MOVES.map((m, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.35, duration: 0.3 }} className="flex items-center gap-2 border border-ink/10 p-1.5">
          <div className="w-8 h-8 shrink-0 relative" style={{ background: m.thumb }}>
            <span className="absolute bottom-0.5 left-0.5 bg-ink/70 text-bone text-[7px] px-1">Ad</span>
          </div>
          <p className="text-[10px] text-ink/75 leading-tight flex-1">{m.copy}</p>
        </motion.div>
      ))}
      <div className="text-[9px] text-coral text-center pt-1 border-t border-ink/10">5 tactics to counter → tap to launch response ads</div>
    </div>
  </Shell>
);

export const CardByVariant = ({ variant }) => {
  const map = { staging: CardStaging, comps: CardComps, tour: CardTour, promo: CardPromo, lead: CardLead, competitor: CardCompetitor };
  const C = map[variant];
  return C ? <C /> : null;
};
